import React, { useState } from "react";
import { Pagination } from "./Pagination";

const fmt = (n, decimals = 1) => {
	if (n == null) return "—";
	const num = parseFloat(n);
	if (Number.isNaN(num)) return "—";
	if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
	if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1) + "K";
	return num.toFixed(decimals);
};

const getRelevanceClass = (score) => {
	const s = parseFloat(score) * 100 || 0;
	if (s >= 85) return "excellent";
	if (s >= 75) return "great";
	if (s >= 60) return "acceptable";
	if (s >= 45) return "poor";
	return "irrelevant";
};

const CategoryBadge = ({ cat }) => {
	const isMeta = cat.categoryType === "META";
	const isTop = cat.maxRelevance;
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 3,
				padding: "2px 7px",
				borderRadius: 10,
				fontSize: 10,
				fontWeight: 500,
				background: isMeta ? "#eff6ff" : "#f0fdf4",
				color: isMeta ? "#1d4ed8" : "#15803d",
				border: `1px solid ${isMeta ? (isTop ? "#3b82f6" : "#bfdbfe") : (isTop ? "#22c55e" : "#bbf7d0")}`,
				outline: isTop ? `2px solid ${isMeta ? "#3b82f6" : "#22c55e"}` : "none",
				outlineOffset: 1,
				whiteSpace: "nowrap",
				maxWidth: 150,
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
			title={`${cat.categoryName} (${cat.categoryType}) — score: ${parseFloat(cat.score || 0).toFixed(3)}${isTop ? " ★ top match" : ""}`}
		>
			<span style={{ opacity: 0.65, fontWeight: 700 }}>{cat.categoryType}</span>
			{" "}{cat.categoryName}
		</span>
	);
};

const ExpandedRow = ({ item, isApp }) => {
	const categories = item.matchedCategories || [];
	return (
		<tr>
			<td colSpan={6} style={{ padding: 0, background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
				<div style={{ padding: "12px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
					{/* All matched categories */}
					<div>
						<span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
							All Matched Categories
						</span>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
							{categories.length > 0
								? categories.map((cat, i) => <CategoryBadge key={i} cat={cat} />)
								: <span style={{ fontSize: 12, color: "#9ca3af" }}>None</span>}
						</div>
					</div>
					{/* Metadata grid */}
					<div style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
						{item.intendedAudience && <Meta label="Intended Audience" value={item.intendedAudience} />}
						{item.geography?.length > 0 && <Meta label="Geography" value={item.geography.join(", ")} />}
						{item.iabMediaSubtype?.length > 0 && <Meta label="Media Subtype" value={item.iabMediaSubtype.join(", ")} />}
						{item.safetyTier?.length > 0 && <Meta label="Safety Tier" value={item.safetyTier.join(", ")} />}
						{item.contentStyles?.length > 0 && <Meta label="Content Styles" value={item.contentStyles.join(", ")} />}
						{item.performanceScore != null && <Meta label="Performance Score" value={parseFloat(item.performanceScore).toFixed(4)} />}
						{item.costPerformanceScore != null && <Meta label="Cost/Performance" value={parseFloat(item.costPerformanceScore).toFixed(4)} />}
					</div>
				</div>
			</td>
		</tr>
	);
};

const Meta = ({ label, value }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
		<span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
		<span style={{ fontSize: 12, color: "#374151" }}>{value}</span>
	</div>
);

const ResultRow = ({ item, isApp, index }) => {
	const [expanded, setExpanded] = useState(false);
	const name = isApp
		? (item.bundleId || item.appName || item.bundle_id || `App #${item.id}`)
		: (item.domainName || item.domain || `Domain #${item.id}`);
	const relevance = parseFloat(item.relevance ?? item.cumulativeRelevance ?? 0);
	const relevanceDisplay = (relevance * 100).toFixed(1);
	const relevanceClass = getRelevanceClass(relevance);
	const composite = parseFloat(item.compositeScore || 0).toFixed(3);
	const reach = fmt(item.domainReach || item.appReach);
	const impressions = fmt(item.totalImpressions ?? item.avgImpressions);
	const categories = item.matchedCategories || [];

	return (
		<>
			<tr
				className={index % 2 === 0 ? "" : "alt-row"}
				onClick={() => setExpanded((v) => !v)}
				style={{ cursor: "pointer" }}
			>
				<td>
					<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<span style={{ fontWeight: 500, color: "#111827", fontSize: 13 }}>{name}</span>
						{item.description && (
							<span style={{ fontSize: 11, color: "#9ca3af", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{item.description}
							</span>
						)}
					</div>
				</td>
				<td style={{ textAlign: "center" }}>
					<span className={`relevance-badge ${relevanceClass}`}>
						{relevanceDisplay}
					</span>
				</td>
				<td style={{ textAlign: "center", fontSize: 12, color: "#374151" }}>
					{composite}
				</td>
				<td style={{ textAlign: "center", fontSize: 12, color: "#374151" }}>
					{reach}
				</td>
				<td style={{ textAlign: "center", fontSize: 12, color: "#374151" }}>
					{impressions}
				</td>
				<td>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
						{categories.slice(0, 3).map((cat, i) => <CategoryBadge key={i} cat={cat} />)}
						{categories.length > 3 && (
							<span style={{
								padding: "2px 7px", borderRadius: 10, fontSize: 10,
								background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb",
							}}>
								+{categories.length - 3}
							</span>
						)}
					</div>
				</td>
			</tr>
			{expanded && <ExpandedRow item={item} isApp={isApp} />}
		</>
	);
};

export const CategoryResultsList = ({
	results = [],
	isLoading = false,
	error = null,
	hasSearched = false,
	subTab = "domains",
	keywords = [],
	currentPage,
	totalPages,
	totalRecords,
	onPageChange,
}) => {
	const isApp = subTab === "apps";

	if (isLoading) {
		return (
			<div className="results-table-container">
				<table className="results-table">
					<tbody>
						<tr className="loading">
							<td colSpan={6} style={{ textAlign: "center", padding: "48px 20px", color: "#6b7280", fontSize: 13 }}>
								<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
									<div className="loading-spinner" />
									Searching {isApp ? "apps" : "domains"} by category...
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	if (error) {
		return (
			<div className="results-table-container">
				<table className="results-table">
					<tbody>
						<tr className="empty">
							<td colSpan={6} style={{ textAlign: "center", padding: 32, color: "#dc2626", fontSize: 13 }}>
								{error}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	if (!hasSearched) {
		return (
			<div className="results-table-container">
				<table className="results-table">
					<tbody>
						<tr className="empty">
							<td colSpan={6} style={{ textAlign: "center", padding: "48px 20px", color: "#9ca3af", fontSize: 13 }}>
								Add at least one include query and click Search to see results.
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	if (results.length === 0) {
		return (
			<div className="results-table-container">
				<table className="results-table">
					<tbody>
						<tr className="empty">
							<td colSpan={6} style={{ textAlign: "center", padding: "48px 20px", color: "#9ca3af", fontSize: 13 }}>
								No results found. Try adjusting your queries or lowering thresholds.
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<div>
			<div className="results-table-container">
				<table className="results-table">
					<thead>
						<tr>
							<th style={{ minWidth: 200 }}>
								{isApp ? "App / Bundle" : "Domain"}
							</th>
							<th className="sortable" style={{ width: 100, textAlign: "center" }}>
								Relevance
							</th>
							<th style={{ width: 110, textAlign: "center" }}>
								Composite Score
							</th>
							<th style={{ width: 90, textAlign: "center" }}>Reach</th>
							<th style={{ width: 100, textAlign: "center" }}>Impressions</th>
							<th>Matched Categories</th>
						</tr>
					</thead>
					<tbody>
						{results.map((item, i) => (
							<ResultRow
								key={item.id ?? `${item.domainName ?? item.bundleId}-${i}`}
								item={item}
								isApp={isApp}
								index={i}
							/>
						))}
					</tbody>
				</table>
			</div>

			{totalPages > 1 && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={onPageChange}
					totalItems={totalRecords}
				/>
			)}

			{/* Keywords strip — shown below results */}
			{keywords.length > 0 && (
				<div className="quick-filters" style={{ marginTop: 10 }}>
					<span style={{ fontSize: 11, fontWeight: 600, color: "#0284c7", textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 6 }}>
						Top keywords:
					</span>
					{keywords.map((kw, i) => (
						<span key={i} className="quick-filter">{kw}</span>
					))}
				</div>
			)}

			<style jsx>{`
        .loading-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #e5e7eb;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .alt-row {
          background: #f9fafb;
        }

        .relevance-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
        }

        .relevance-badge.excellent {
          background: #dcfce7;
          color: #166534;
        }

        .relevance-badge.great {
          background: #d1fae5;
          color: #065f46;
        }

        .relevance-badge.acceptable {
          background: #fef3c7;
          color: #92400e;
        }

        .relevance-badge.poor {
          background: #fee2e2;
          color: #991b1b;
        }

        .relevance-badge.irrelevant {
          background: #f3f4f6;
          color: #6b7280;
        }
      `}</style>
		</div>
	);
};
